import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Search, Plus, Eye, Ban, RotateCcw, ArrowLeft, Printer, Award } from "lucide-react";
import { CursoCurto } from "@/pages/admin/finance/common";
import { toast } from "sonner";
import { Pager } from "@/components/Pager";
import CertificateView from "@/components/CertificateView";

import { confirmar } from "@/components/ConfirmDialog";
const errMsg = (e) => formatApiError(e.response?.data?.detail);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const selCls = "h-10 rounded-md border border-slate-200 bg-white px-3 text-sm";

function Status({ r }) {
  const [label, cls] = r.revoked ? ["Cancelado", "bg-red-50 text-red-700"] : r.expired ? ["Vencido", "bg-amber-50 text-amber-700"] : ["Válido", "bg-green-50 text-green-700"];
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cls}`}>{label}</span>;
}

/** Todos os certificados emitidos (automáticos e manuais). */
export default function CertIssued() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [f, setF] = useState({ q: "", course_id: "", status: "" });
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [dlg, setDlg] = useState(null); // emissão manual
  const [students, setStudents] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/certificates", { params: { ...f, page, limit: 50 } }).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({ total: 0, rows: [] }); });
  }, [f, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setF((p) => ({ ...p, q })); setPage(1); }, 300); return () => clearTimeout(t); }, [q]);

  const openManual = () => {
    setDlg({ course_id: "", student_id: "", search: "" });
    if (students === null) api.get("/students").then((r) => setStudents(r.data)).catch(() => setStudents([]));
  };
  const matches = useMemo(() => {
    if (!dlg || !students || dlg.search.trim().length < 2) return [];
    const s = dlg.search.trim().toLowerCase();
    return students.filter((x) => (x.name || "").toLowerCase().includes(s) || (x.email || "").toLowerCase().includes(s)).slice(0, 8);
  }, [dlg, students]);
  const issue = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/certificates", { student_id: dlg.student_id, course_id: dlg.course_id }); toast.success(`Certificado emitido (${r.data.code}).`); setDlg(null); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <div className="p-4 sm:p-8 max-w-6xl">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
        <h1 className="font-display text-2xl font-bold text-slate-900">Certificados emitidos</h1>
        <Button onClick={openManual} className="text-white" style={{ background: "#1E9E2A" }} data-testid="btn-issue-cert"><Plus className="h-4 w-4 mr-1.5" /> Emitir certificado</Button>
      </div>
      <p className="text-sm text-slate-500 mb-5">Todos os certificados ficam guardados aqui. Eles são gerados sozinhos quando o aluno conclui o curso, ou emitidos por você manualmente.</p>

      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aluno, curso ou código..." className="pl-9 h-10" data-testid="cert-search" />
        </div>
        <select value={f.course_id} onChange={(e) => { setF((p) => ({ ...p, course_id: e.target.value })); setPage(1); }} className={selCls}>
          <option value="">Todos os cursos</option>
          {courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
        </select>
        <select value={f.status} onChange={(e) => { setF((p) => ({ ...p, status: e.target.value })); setPage(1); }} className={selCls}>
          <option value="">Todos</option><option value="valid">Não cancelados</option><option value="revoked">Cancelados</option>
        </select>
      </div>

      {data === null ? <div className="py-12 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto iep-scroll">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
              <th className="px-4 py-3">Aluno</th><th className="px-3 py-3">Curso</th><th className="px-3 py-3 hidden md:table-cell">Código</th>
              <th className="px-3 py-3 hidden sm:table-cell">Emitido em</th><th className="px-3 py-3">Situação</th><th className="px-4 py-3 text-right">Ações</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {data.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">Nenhum certificado encontrado.</td></tr>}
              {data.rows.map((r) => (
                <tr key={r.id} data-testid={`cert-row-${r.id}`}>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{r.student_name}{r.manual && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">manual</span>}</td>
                  <td className="px-3 py-2.5 text-slate-600"><CursoCurto titulo={r.course_title} /></td>
                  <td className="px-3 py-2.5 hidden md:table-cell font-mono text-xs text-slate-500">{r.code}</td>
                  <td className="px-3 py-2.5 hidden sm:table-cell text-slate-600">{fmtDate(r.issued_at)}</td>
                  <td className="px-3 py-2.5"><Status r={r} /></td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/admin/certificados/emitidos/${r.id}`)}><Eye className="h-3.5 w-3.5 mr-1" /> Ver / imprimir</Button>{" "}
                    {r.revoked
                      ? <Button size="sm" variant="ghost" onClick={() => act(() => api.post(`/admin/certificates/${r.id}/restore`), "Certificado reativado.")}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Reativar</Button>
                      : <Button size="sm" variant="ghost" className="text-red-600" onClick={async () => (await confirmar(`Cancelar o certificado de ${r.student_name}? A validação pública passa a mostrar que ele foi cancelado.`)) && act(() => api.post(`/admin/certificates/${r.id}/revoke`), "Certificado cancelado.")}><Ban className="h-3.5 w-3.5 mr-1" /> Cancelar</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} perPage={50} total={data.total} onChange={setPage} />
        </div>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Award className="h-5 w-5 text-[#1E9E2A]" /> Emitir certificado</DialogTitle>
            <DialogDescription>Emissão manual: não exige que o aluno tenha concluído as aulas. O aluno precisa já ter ativado a conta.</DialogDescription></DialogHeader>
          {dlg && (
            <div className="space-y-4 py-1">
              <div className="space-y-1.5"><Label>Curso</Label>
                <select value={dlg.course_id} onChange={(e) => setDlg((p) => ({ ...p, course_id: e.target.value }))} className={`${selCls} w-full`}>
                  <option value="">Escolha o curso…</option>{courses.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
                </select></div>
              <div className="space-y-1.5"><Label>Aluno</Label>
                <Input value={dlg.search} onChange={(e) => setDlg((p) => ({ ...p, search: e.target.value, student_id: "" }))} placeholder="Digite o nome ou e-mail…" />
                {students === null && <p className="text-xs text-slate-400">Carregando alunos…</p>}
                {matches.length > 0 && !dlg.student_id && (
                  <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-48 overflow-y-auto">
                    {matches.map((s) => <button key={s.id} type="button" onClick={() => setDlg((p) => ({ ...p, student_id: s.id, search: s.name }))} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"><div className="font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.email}</div></button>)}
                  </div>
                )}
                {dlg.student_id && <p className="text-xs text-green-700">Aluno selecionado.</p>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
            <Button disabled={!dlg?.course_id || !dlg?.student_id || busy} onClick={issue} className="text-white" style={{ background: "#1E9E2A" }}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Emitir"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Ver e imprimir um certificado emitido. */
export function CertIssuedView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/certificates/${id}`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); navigate("/admin/certificados/emitidos"); }); }, [id, navigate]);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-8">
      <div className="flex items-center justify-between mb-5 print:hidden">
        <button onClick={() => navigate("/admin/certificados/emitidos")} className="flex items-center gap-1.5 text-sm font-semibold text-[#1E9E2A]"><ArrowLeft className="h-4 w-4" /> Voltar aos certificados</button>
        <Button onClick={() => window.print()} className="text-white" style={{ background: "#1E9E2A" }}><Printer className="h-4 w-4 mr-1.5" /> Imprimir / Salvar PDF</Button>
      </div>
      {d.certificate.revoked && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-2 print:hidden">Este certificado está <strong>cancelado</strong>. A validação pública mostra que ele não vale.</div>}
      <CertificateView template={d.template} fields={d.fields} />
    </div>
  );
}
